/**
 * Export API Service
 *
 * Handles data export operations for alerts and products.
 * Services are stateless and return promises - UI concerns belong in hooks.
 *
 * @see ../ARCHITECTURE.md for design principles
 */

import { authenticatedFetch } from '../../utils/authenticated-fetch';
import { ExportApiError, type ExportParams, type ExportResult } from './types';

/**
 * Export data (alerts or products) in the specified format
 *
 * @param params - Export configuration
 * @returns Blob and filename for download
 * @throws ExportApiError if the operation fails
 */
export async function exportData(params: ExportParams): Promise<ExportResult> {
  const { type, format, fields, tab } = params;

  // Build query parameters
  const queryParams = new URLSearchParams({ format });

  // Only include fields and tab for alerts export
  if (type === 'alerts') {
    if (fields && fields.length > 0) {
      queryParams.append('fields', fields.join(','));
    }
    if (tab !== undefined) {
      queryParams.append('tab', tab.toString());
    }
  }

  const endpoint = `/api/export/${type}?${queryParams.toString()}`;
  const response = await authenticatedFetch(endpoint, { method: 'GET' });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Export failed (${response.status})`;

    try {
      const errorData = JSON.parse(errorText);
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If not JSON, use the text as-is if it's not too long
      if (errorText.length > 0 && errorText.length < 200) {
        errorMessage = errorText;
      }
    }

    throw new ExportApiError(errorMessage, response.status);
  }

  // Get the filename from the response headers or generate one
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = `stock-${type}-${new Date().toISOString().split('T')[0]}.${format}`;

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();

  return { blob, filename };
}

/**
 * Download a blob as a file
 *
 * Creates a temporary link element to trigger browser download.
 *
 * @param blob - The blob to download
 * @param filename - The filename for the download
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Export and download data in one operation
 *
 * Combines exportData and downloadBlob for convenience.
 *
 * @param params - Export configuration
 * @throws ExportApiError if the operation fails
 */
export async function exportAndDownload(params: ExportParams): Promise<void> {
  const { blob, filename } = await exportData(params);
  downloadBlob(blob, filename);
}
